import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationInfoService } from '@app/core/guards/authentication-info.service';
import { AccessRequirement } from '@app/core/models/access-requirement.model';
import { TableColumnsConfigHelper } from '@app/core/models/configuration/table-columns-config-helper';
import { TableColumnsConfigKey } from '@app/core/models/configuration/table-columns-config-key.model';
import { GroupDetailModel } from '@app/core/models/groups/group-detail.model';
import { GroupTypeModel } from '@app/core/models/groups/group-type.model';
import { GroupModel } from '@app/core/models/groups/group.model';
import { ProfessionTypeModel } from '@app/core/models/groups/profession-type.model';
import { CompanyRef } from '@app/core/models/referentials/company.model';
import { ReferentialModel } from '@app/core/models/referentials/referential.model';
import { ReferentialsModel } from '@app/core/models/referentials/referentials.model';
import { UserModel } from '@app/core/models/users/user.model';
import { GroupService } from '@app/core/services/group.service';
import { UserService } from '@app/core/services/user.service';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { ModalHelper } from '@app/shared/models/modal-helper.model';
import { MultiSelectModel } from '@app/shared/models/multi-select.model';
import {
  TableActionIcon,
  TableRowAction,
} from '@app/shared/models/table-action-type.model';
import * as _ from 'lodash';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AdministrationDetailBase } from '../../administration-detail-base';
import { GroupTransferUsersComponent } from '../group-transfer-users/group-transfer-users.component';

@Component({
  selector: 'app-group-detail',
  templateUrl: './group-detail.component.html',
  styleUrls: ['./group-detail.component.scss'],
})
export class GroupDetailComponent
  extends AdministrationDetailBase
  implements OnInit, OnDestroy
{
  // Model business
  groupDetailModel: GroupDetailModel;
  groups: GroupModel[] = [];
  controlGroupsDropdownListData: GroupModel[] = [];
  operationalGroupsDropdownListData: GroupModel[] = [];
  users: UserModel[] = [];
  groupUsers: UserModel[] = [];
  isOperationalTypeSelected = false;
  isGroupIdDefined = false;
  isGroupOperationalTypeSelected = false;
  isGroupControlOrOperationalContributorTypeSelected = false;
  isGroupControlTypeSelected = false;
  isGroupBackupTypeSelected = false;
  canDisable = false;

  // model technique
  typeDropdownListData: ReferentialModel[] = [];
  typeSelectedItems = [];
  typeDropdownSettings: IDropdownSettings = {};

  professionDropdownListData: ReferentialModel[] = [];
  professionSelectedItems = [];
  professionDropdownSettings: IDropdownSettings = {};

  marqueDropdownListData: CompanyRef[] = [];
  marqueSelectedItems = [];
  marqueDropdownSettings: IDropdownSettings = {};

  typologieDropdownListData: ReferentialModel[] = [];
  typologieSelectedItems = [];
  typologieDropdownSettings: IDropdownSettings = {};

  arboDropdownListData: ReferentialModel[] = [];
  arboSelectedItems = [];
  arboDropdownSettings: IDropdownSettings = {};

  userDropdownListData: UserModel[];
  userSelectedItems = [];
  userDropdownSettings: IDropdownSettings = {};

  userReferentSelectedItems = [];

  // form
  groupDetailForm: UntypedFormGroup;
  submitted = false;

  // affectation
  userDropDownSettings: IDropdownSettings;
  dataItems: UserModel[] = [];

  dataReferentItems: UserModel[] = [];
  userReferentDropDownSettings: IDropdownSettings;

  columnsDefinition = {};
  tableActions: TableRowAction[] = [];
  exportFileName: string;

  singleSelectionDropdownSettings: IDropdownSettings = {};
  controlGroupsSelectedItems = [];
  operationalGroupsSelectedItems = [];
  configKey: string = TableColumnsConfigKey.GROUPUSERS;

  isAdministrator: boolean = false;

  public onExportExcelCaller: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    groupService: GroupService,
    private userService: UserService,
    private formBuilder: UntypedFormBuilder,
    public dialog: MatDialog,
    private changeDetectorRefs: ChangeDetectorRef,
    private authInfoService: AuthenticationInfoService,
    private multiSelectModel: MultiSelectModel
  ) {
    super(groupService);
  }

  onGroupFetched(groupType: GroupTypeModel, groups: GroupModel[]): void {
    switch (groupType) {
      case GroupTypeModel.OPERATIONAL:
        this.operationalGroupsDropdownListData = groups;
        break;
      case GroupTypeModel.CONTROL:
      case GroupTypeModel.OPERATIONCONTRIBUTOR:
        this.controlGroupsDropdownListData = _.filter(
          groups,
          (g) => !g.IsLinked || g.Id == this.groupDetailModel.LinkedGroupId
        );
        break;
    }
  }

  ngOnInit(): void {
    this.isAdministrator = this.authInfoService.authentificatedUser
      ? this.authInfoService.authentificatedUser.hasGrant(
          AccessRequirement.AdministrationMembership
        )
      : false;

    this.userService
      .getUsers()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((users) => {
        this.users = _.filter(users, (u) => u.IsActive);
        this.setUserDropDownSelectedItems();
      });

    this.groupDetailModel = new GroupDetailModel();
    this.groupDetailModel.CanBeDisabled = false;
    this.groupDetailModel.CanBeEnabled = false;

    this.route.data.subscribe((data) => {
      this.groupDetailModel = data?.group ?? new GroupDetailModel();
      const referentials: ReferentialsModel = data.referentials;

      this.typeDropdownListData = referentials.GroupTypes.filter(
        (t) => t.Id != GroupTypeModel.ALL
      );
      this.professionDropdownListData = referentials.ProfessionTypes.filter(
        (t) => t.Id != ProfessionTypeModel.Aucun
      );
      this.marqueDropdownListData = referentials.Company;
      this.typologieDropdownListData = referentials.TypologieType;
      this.arboDropdownListData = referentials.TreeLevelType;

      this.exportFileName = `Utilisateurs_groupe_[${this.groupDetailModel.Name}]`;
    });

    // cas modif récupérer les users du groupe
    if (this.groupDetailModel && this.groupDetailModel.Id) {
      this.groupService
        .getGroupUsersById(this.groupDetailModel?.Id)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((groupUsers: UserModel[]) => {
          this.groupUsers = groupUsers;
          this.groupDetailModel.Users = this.groupUsers;

          this.dataItems = this.groupDetailModel?.Users;
          this.dataReferentItems = [...this.groupDetailModel?.Users];

          this.setTypeDropDownSelectedReferentItems();

          // en cas de modification on affiche que les utilisateurs qui n'appartient pas au groupe !
          this.users = this.users.filter(
            (u) => !this.groupUsers.map((gu) => gu.Id).includes(u.Id)
          );
          this.canDisable =
            this.groupDetailModel.CanBeDisabled &&
            !this.containsCurrentUser(this.groupUsers);
          this.userDropdownListData = this.users;
        });
    }

    this.typeDropdownSettings = this.multiSelectModel.getSingleSelection(true);
    this.professionDropdownSettings =
      this.multiSelectModel.getSingleSelection(true);
    this.singleSelectionDropdownSettings = this.multiSelectModel.getSettings(
      'Id',
      'Name',
      true,
      true,
      true
    );
    this.marqueDropdownSettings = this.multiSelectModel.getSettings(
      'Code',
      'Label',
      true
    );
    this.typologieDropdownSettings =
      this.multiSelectModel.getSingleSelection(true);

    this.arboDropdownSettings = this.multiSelectModel.getSingleSelection(false);
    this.arboDropdownSettings =
      this.multiSelectModel.getAllowSearchFilter(true);

    this.singleSelectionDropdownSettings.allowRemoteDataSearch = true;

    this.isGroupIdDefined = !_.isNil(this.route.snapshot.paramMap.get('id'));
    this.isGroupOperationalTypeSelected =
      this.groupDetailModel?.TypeId == GroupTypeModel.OPERATIONAL;
    this.isGroupControlOrOperationalContributorTypeSelected =
      this.groupDetailModel?.TypeId == GroupTypeModel.CONTROL ||
      this.groupDetailModel?.TypeId == GroupTypeModel.OPERATIONCONTRIBUTOR;
    this.isGroupControlTypeSelected =
      this.groupDetailModel?.TypeId == GroupTypeModel.CONTROL;
    this.isGroupBackupTypeSelected =
      this.groupDetailModel?.TypeId == GroupTypeModel.BACKUP;

    this.buildFormControls();
    this.setUserDropDownSelectedItems();
    this.setTypeDropDownSelectedItems();
    this.setControlGroupDropDownSelectedItems();
    this.setProfessionTypeDropDownSelectedItems();
    this.setBackupSelectedItems();
    this.setDataTableConfig();
    this.groupDetailForm.controls.IsVoucher.valueChanges.subscribe(
      (isVoucher) => {
        if (isVoucher) {
          this.groupDetailForm.controls.CanCreateInPPR.patchValue(false);
          this.groupDetailForm.controls.IsPrescriptor.patchValue(false);
        }
      }
    );
  }

  containsCurrentUser(users: UserModel[]): boolean {
    return (
      !_.isNil(users) &&
      users.length > 0 &&
      users
        .map((u) => u.Id)
        .includes(this.authInfoService.authentificatedUser.Id)
    );
  }

  setDataTableConfig() {
    this.tableActions.push(
      new TableRowAction(
        TableActionIcon.DELETE,
        "Supprimer l'utilisateur du groupe",
        this.onDelete,
        null,
        null,
        !this.isAdministrator
      )
    );
    this.columnsDefinition =
      new TableColumnsConfigHelper().getColumnsDefinition(this.configKey);
  }

  onDelete = (data) => {
    this.deleteUserFromGroup(data);
    this.refreshCanDisable();
  };

  deleteUserFromGroup(data) {
    this.dataItems = this.dataItems.filter((t) => t.Id != data.Id);
    this.userSelectedItems = this.userSelectedItems.filter(
      (s) => s.Id != data.Id
    );
    if (!this.userDropdownListData.find((x) => x.Id == data.Id)) {
      this.userDropdownListData.push(data);
      this.refreshUserDropdownListData();
    }

    this.refreshDeletedGroupReferent(data);
  }

  refreshDeletedGroupReferent(data) {
    this.dataReferentItems = this.dataReferentItems.filter(
      (t) => t.Id != data.Id
    );
    if (data.Id == this.groupDetailModel.SupervisorUserId) {
      this.groupDetailModel.SupervisorUserId = null;
      this.groupDetailForm.controls.Supervisor.patchValue(null);
    }
  }

  setUserDropDownSelectedItems() {
    this.userDropDownSettings = new MultiSelectModel().getSettings(
      'Id',
      'FullName',
      false,
      true,
      true,
      true,
      'Rechercher',
      6
    );
    this.userDropdownListData = this.users;

    this.userReferentDropDownSettings = new MultiSelectModel().getSettings(
      'Id',
      'FullName',
      true,
      true,
      true,
      false,
      'Rechercher',
      6
    );
  }

  setTypeDropDownSelectedItems() {
    this.typeSelectedItems = this.typeDropdownListData.filter(
      (x) => x.Id == this.groupDetailModel?.TypeId
    );
  }

  setTypeDropDownSelectedReferentItems() {
    this.userReferentSelectedItems = this.dataReferentItems.filter(
      (x) => x.Id == this.groupDetailModel?.SupervisorUserId
    );
  }

  setControlGroupDropDownSelectedItems() {
    if (_.isNil(this.groupDetailModel?.LinkedGroupId)) {
      return;
    }
    const linkedGroup = [
      {
        Id: this.groupDetailModel?.LinkedGroupId,
        Name: this.groupDetailModel?.LinkedGroupName,
      },
    ];
    this.controlGroupsSelectedItems = linkedGroup;
    this.operationalGroupsSelectedItems = linkedGroup;
  }

  setProfessionTypeDropDownSelectedItems() {
    this.professionSelectedItems = this.professionDropdownListData.filter(
      (x) => x.Id == this.groupDetailModel?.ProfessionTypeId
    );
  }

  setBackupSelectedItems() {
    this.marqueSelectedItems = this.marqueDropdownListData.filter(
      (x) => x.Code == this.groupDetailModel?.CompanyCode
    );
    this.typologieSelectedItems = this.typologieDropdownListData.filter(
      (x) => x.Id == this.groupDetailModel?.Typology
    );
    this.arboSelectedItems = this.groupDetailModel?.TreeLevelValues;
  }

  buildFormControls() {
    this.groupDetailForm = this.formBuilder.group(
      {
        Name: [
          {
            value: this.groupDetailModel?.Name,
            disabled: !this.isAdministrator,
          },
          Validators.required,
        ],
        IsActive: [
          {
            value: this.groupDetailModel?.IsActive,
            disabled: !this.isAdministrator,
          },
          Validators.required,
        ],
        Supervisor: [
          {
            value: [this.groupDetailModel?.SupervisorUserId],
            disabled: !this.isAdministrator,
          },
        ],
        CanManageInvoiceAGI: [this.groupDetailModel?.CanManageInvoiceAGI],
        Type: new UntypedFormControl(
          {
            disabled: this.isGroupIdDefined && !this.isAdministrator,
            value: this.groupDetailModel?.Type,
          },
          [Validators.required]
        ),
        ControlGroup: new UntypedFormControl({
          value: this.groupDetailModel?.LinkedGroupId,
          disabled: !this.isAdministrator,
        }),
        IsVoucher: [
          {
            value: this.groupDetailModel?.IsVoucher,
            disabled: !this.isAdministrator,
          },
        ],
        ProfessionType: [
          {
            value: this.groupDetailModel?.ProfessionTypeId,
            disabled: !this.isAdministrator,
          },
        ],
        Users: [],
        VoucherAnalyticalCode: [
          {
            value: this.groupDetailModel?.VoucherAnalyticalCode,
            disabled: !this.isAdministrator,
          },
        ],
        VoucherAnalyticalSection: [
          {
            value: this.groupDetailModel?.VoucherAnalyticalSection,
            disabled: !this.isAdministrator,
          },
        ],
        VoucherDepartment: [
          {
            value: this.groupDetailModel?.VoucherDepartment,
            disabled: !this.isAdministrator,
          },
        ],
        VoucherComment: [
          {
            value: this.groupDetailModel?.VoucherComment,
            disabled: !this.isAdministrator,
          },
        ],
        CanCreateInPPR: [
          {
            value: this.groupDetailModel?.CanCreateInPPR,
            disabled: !this.isAdministrator,
          },
        ],
        IsPrescriptor: [
          {
            value: this.groupDetailModel?.IsPrescriptorGroup,
            disabled: !this.isAdministrator,
          },
        ],
        Marque: [
          {
            value: this.groupDetailModel?.CompanyCode,
            disabled: !this.isAdministrator,
          },
        ],
        Typologie: [
          {
            value: this.groupDetailModel?.Typology,
            disabled: !this.isAdministrator,
          },
        ],
        Arborescence: [
          {
            value: this.groupDetailModel?.TreeLevelValues,
            disabled: !this.isAdministrator,
          },
        ],
      },
      { validators: this.requiredForOperationValidator() }
    );
  }

  setGroupDetailModel() {
    const groupDetailModel: GroupDetailModel = Object.assign(
      {},
      this.groupDetailModel
    );
    groupDetailModel.Name = this.formControls.Name.value;
    groupDetailModel.IsActive = this.formControls.IsActive.value;
    groupDetailModel.TypeId = _.head(this.typeSelectedItems)['Id'];
    groupDetailModel.Type = _.head(this.typeSelectedItems)['Label'];
    groupDetailModel.SupervisorUserId = !_.isEmpty(
      this.userReferentSelectedItems
    )
      ? _.head(this.userReferentSelectedItems)['Id']
      : null;
    groupDetailModel.SupervisorUser = !_.isEmpty(this.userReferentSelectedItems)
      ? _.head(this.userReferentSelectedItems)['FullName']
      : null;
    groupDetailModel.Users = this.dataItems;

    if (groupDetailModel.TypeId == GroupTypeModel.OPERATIONAL) {
      groupDetailModel.IsVoucher = this.formControls.IsVoucher.value;
      groupDetailModel.ProfessionTypeId = !_.isEmpty(
        this.professionSelectedItems
      )
        ? _.head(this.professionSelectedItems)['Id']
        : null;
      groupDetailModel.ProfessionType = !_.isEmpty(this.professionSelectedItems)
        ? _.head(this.professionSelectedItems)['Label']
        : null;
      groupDetailModel.LinkedGroupId =
        !_.isNil(this.controlGroupsSelectedItems) &&
        this.controlGroupsSelectedItems.length > 0
          ? _.head(this.controlGroupsSelectedItems)['Id']
          : null;
      groupDetailModel.CanManageInvoiceAGI =
        this.formControls.CanManageInvoiceAGI.value ?? false;
      if (groupDetailModel.IsVoucher) {
        groupDetailModel.VoucherAnalyticalCode =
          this.formControls.VoucherAnalyticalCode.value;
        groupDetailModel.VoucherAnalyticalSection =
          this.formControls.VoucherAnalyticalSection.value;
        groupDetailModel.VoucherDepartment =
          this.formControls.VoucherDepartment.value;
        groupDetailModel.VoucherComment =
          this.formControls.VoucherComment.value;
      }
      groupDetailModel.CanCreateInPPR = this.formControls.CanCreateInPPR.value;
      groupDetailModel.IsPrescriptorGroup =
        this.formControls.IsPrescriptor.value;
    } else if (
      groupDetailModel.TypeId == GroupTypeModel.CONTROL ||
      groupDetailModel.TypeId == GroupTypeModel.OPERATIONCONTRIBUTOR
    ) {
      groupDetailModel.LinkedGroupId =
        !_.isNil(this.operationalGroupsSelectedItems) &&
        this.operationalGroupsSelectedItems.length > 0
          ? _.head(this.operationalGroupsSelectedItems)['Id']
          : null;
    } else if (groupDetailModel.TypeId == GroupTypeModel.BACKUP) {
      groupDetailModel.CompanyCode =
        !_.isNil(this.marqueSelectedItems) &&
        this.marqueSelectedItems.length > 0
          ? _.head(this.marqueSelectedItems)['Code']
          : null;

      groupDetailModel.Typology =
        !_.isNil(this.typologieSelectedItems) &&
        this.typologieSelectedItems.length > 0
          ? _.head(this.typologieSelectedItems)['Id']
          : null;

      groupDetailModel.TreeLevelValues =
        !_.isNil(this.arboSelectedItems) && this.arboSelectedItems.length > 0
          ? this.arboSelectedItems
          : null;
    }

    this.isGroupIdDefined
      ? this.updateGroupe(this.groupDetailModel.Id, groupDetailModel)
      : this.createGroupe(groupDetailModel);
  }

  get IsVoucher(): boolean {
    return this.formControls.IsVoucher.value;
  }

  updateGroupe(Id: number, groupDetailModel: GroupDetailModel) {
    if (!this.isAdministrator) {
      return;
    }

    this.groupService
      .updateGroupById(Id, groupDetailModel)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (_) => {
          this.authInfoService.refresh();
          this.returnBackToGroupList();
        },
        (error) => {
          if (error.status === 403) {
            this.groupDetailModel.CanBeDisabled = false;
            this.groupDetailModel.IsActive = true;
            this.formControls.IsActive.patchValue(true);
          }
        }
      );
  }

  createGroupe(groupDetailModel: GroupDetailModel) {
    this.groupService
      .createGroup(groupDetailModel)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => {
        this.authInfoService.refresh();
        this.returnBackToGroupList();
      });
  }

  onSubmit() {
    if (!this.isAdministrator) {
      return;
    }
    this.submitted = true;
    if (this.groupDetailForm.valid) {
      this.onUpdate()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((isConfirmed) => {
          if (isConfirmed) {
            this.setGroupDetailModel();
          }
        });
    }
  }

  onUpdate(): Observable<any> {
    const confimModal = new ModalHelper().getConfirmationConfig(
      'Êtes-vous sûr de vouloir effectuer cette modification ?'
    );
    const dialog = this.dialog.open(ModalComponent, confimModal);
    return dialog.afterClosed().pipe(takeUntil(this.unsubscribe$));
  }

  onItemSelect() {
    this.isGroupOperationalTypeSelected =
      this.typeSelectedItems &&
      this.typeSelectedItems.length > 0 &&
      _.head(this.typeSelectedItems)['Id'] == GroupTypeModel.OPERATIONAL;

    this.isGroupControlOrOperationalContributorTypeSelected =
      this.typeSelectedItems &&
      this.typeSelectedItems.length > 0 &&
      (_.head(this.typeSelectedItems)['Id'] == GroupTypeModel.CONTROL ||
        _.head(this.typeSelectedItems)['Id'] ==
          GroupTypeModel.OPERATIONCONTRIBUTOR);

    this.isGroupControlTypeSelected =
      this.typeSelectedItems &&
      this.typeSelectedItems.length > 0 &&
      _.head(this.typeSelectedItems)['Id'] == GroupTypeModel.CONTROL;

    this.isGroupBackupTypeSelected =
      this.typeSelectedItems &&
      this.typeSelectedItems.length > 0 &&
      _.head(this.typeSelectedItems)['Id'] == GroupTypeModel.BACKUP;
  }

  refreshUserDropdownListData() {
    this.userDropdownListData = this.userDropdownListData.slice();
    this.changeDetectorRefs.detectChanges();
  }

  refreshTable() {
    this.dataItems = this.dataItems.slice();
    this.dataReferentItems = this.dataReferentItems.slice();
    this.refreshCanDisable();
    this.changeDetectorRefs.detectChanges();
  }

  refreshCanDisable() {
    this.canDisable =
      this.groupDetailModel.CanBeDisabled &&
      !this.containsCurrentUser(this.dataItems);
    if (!this.formControls.IsActive.value && !this.canDisable) {
      this.groupDetailForm.patchValue({ IsActive: true });
    }
  }

  onSelectUser(item: any, action: string): void {
    switch (action) {
      case 'select':
        const user = _.find(this.users, { Id: item.Id });
        this.dataItems.push(user);
        this.dataReferentItems.push(user);
        break;
      case 'deselect':
        this.dataItems = this.dataItems.filter((t) => t.Id != item.Id);
        this.dataReferentItems = this.dataReferentItems.filter(
          (t) => t.Id != item.Id
        );
        break;
      case 'selectall':
        this.dataItems = [];
        this.dataItems.push(...this.groupUsers, ...this.users);
        this.dataReferentItems = [];
        this.dataReferentItems.push(...this.groupUsers, ...this.users);
        break;
      case 'deselectall':
        this.dataItems = [];
        this.dataItems.push(...this.groupUsers);
        this.dataReferentItems = [];
        this.dataReferentItems.push(...this.groupUsers);
        break;
    }
    this.refreshTable();
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.groupDetailForm.controls;
  }

  onChangeStatus = () => {};

  onForward = () => {
    const transferModal = new ModalHelper().getTransferUsersModalConfig(
      this.groupDetailModel.Id.toString(),
      this.groupDetailModel.Name
    );
    const dialog = this.dialog.open(GroupTransferUsersComponent, transferModal);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) {
          this.userDropdownListData = this.dataItems;
          this.userSelectedItems = [];
          this.dataItems = [];
        }
      });
  };

  requiredForOperationValidator() {
    return (form: UntypedFormGroup): { [key: string]: boolean } | null => {
      const type = _.head<GroupModel>(form.controls.Type.value)?.Id;
      const profession = form.controls.ProfessionType.value;

      const marque = form.controls.Marque.value;
      const typologie = form.controls.Typologie.value;
      const arbo = form.controls.Arborescence.value;

      if (
        type == GroupTypeModel.OPERATIONAL &&
        (_.isNil(profession) || _.isEmpty(profession))
      ) {
        return { requiredForOperationValidator: true };
      }
      if (
        type == GroupTypeModel.BACKUP &&
        (_.isNil(marque) ||
          _.isEmpty(marque) ||
          _.isNil(typologie) ||
          _.isEmpty(typologie) ||
          _.isNil(arbo) ||
          _.isEmpty(arbo))
      ) {
        return { requiredForOperationValidator: true };
      }

      return null;
    };
  }

  onCancel(): void {
    this.groupDetailForm.reset();
    this.returnBackToGroupList();
  }

  returnBackToGroupList() {
    this.router.navigate(['administration/groups']);
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
  }

  exportUsers() {
    this.onExportExcelCaller.next();
  }
}
